import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Search, Clock, Users, Zap, Loader2, ExternalLink } from "lucide-react";
import { useHiringManagerSearch, type Contact } from "../hooks/useHiringManagerSearch";
import { SearchProgress } from "../components/SearchProgress";
import { exportHiringManagerPdf } from "../lib/exportHiringManagerPdf";

const GHOST_PROMPTS = [
  "Paste a job URL here...",
  "Or paste the full job description...",
  "Or drop in both for the best results...",
  "https://company.com/careers/senior-engineer",
];

const DOT_BG = "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.07'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")";

function ContactCard({ c }: { c: Contact }) {
  return (
    <div className="px-5 py-4 border-b border-white/10 last:border-0">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
            style={{ background: "linear-gradient(to bottom right, #FDE047, #fb923c)", color: "#1E3A8A" }}>
            {c.rank}
          </span>
          <div>
            <p className="text-white font-black text-sm">{c.full_name}</p>
            <p className="text-white/60 text-xs">{c.title}</p>
          </div>
        </div>
        {c.linkedin_url && (
          <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: "rgba(253,224,71,0.15)", border: "1px solid rgba(253,224,71,0.4)", color: "#FDE047" }}>
            <ExternalLink size={11} />
            LinkedIn
          </a>
        )}
      </div>
      {(c.summary || c.profile_summary) && (
        <p className="mt-2 ml-10 text-xs text-white/55 leading-relaxed">{c.summary ?? c.profile_summary}</p>
      )}
      {c.outreach_tip && (
        <p className="mt-1.5 ml-10 text-xs font-semibold text-yellow-300">
          Tip: {c.outreach_tip}
        </p>
      )}
    </div>
  );
}

export default function HiringManagerId2() {
  const [query, setQuery] = useState("");
  const [ghostText, setGhostText] = useState("");
  const { loading, queued, result, error, progress, progressStage, startedAt, search, reset } = useHiringManagerSearch();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    document.title = "HiringManagerID — Find the Hiring Manager for ANY Job in Minutes";
  }, []);

  useEffect(() => {
    let i = 0, c = 0, deleting = false;
    function tick() {
      if (query.length > 0) { setGhostText(""); timeoutRef.current = setTimeout(tick, 500); return; }
      const cur = GHOST_PROMPTS[i];
      if (!deleting) {
        setGhostText(cur.slice(0, ++c));
        if (c >= cur.length) { deleting = true; timeoutRef.current = setTimeout(tick, 1600); return; }
        timeoutRef.current = setTimeout(tick, 70);
      } else {
        setGhostText(cur.slice(0, --c));
        if (c <= 0) { deleting = false; i = (i + 1) % GHOST_PROMPTS.length; timeoutRef.current = setTimeout(tick, 300); return; }
        timeoutRef.current = setTimeout(tick, 35);
      }
    }
    tick();
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [query]);

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: `${DOT_BG}, linear-gradient(135deg, #1e1b4b 0%, #1E3A8A 40%, #1e3a5f 100%)`, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{`
        @keyframes pulse-orb { 0%,100%{opacity:.12}50%{opacity:.22} }
        .orb { border-radius:9999px; position:absolute; pointer-events:none; filter:blur(90px); animation:pulse-orb 5s ease-in-out infinite; }
        @keyframes spin { to { transform: rotate(360deg) } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>

      <div className="orb w-96 h-96 -top-20 -right-20" style={{ background: "rgba(253,224,71,.15)", animationDelay: "0s" }} />
      <div className="orb w-72 h-72 bottom-0 left-0" style={{ background: "rgba(99,102,241,.2)", animationDelay: "2s" }} />
      <div className="orb w-56 h-56 top-1/3 left-1/4" style={{ background: "rgba(30,58,138,.3)", animationDelay: "1s" }} />

      <header className="relative z-10 px-6 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow" style={{ background: "#FDE047" }}>
            <Target size={20} color="#1E3A8A" strokeWidth={2.5} />
          </div>
          <span className="text-white font-black text-xl tracking-tight">HiringManagerID</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/hiring-manager-id" className="hidden sm:inline text-sm font-medium text-blue-200 hover:text-white transition-colors">
            Original view
          </Link>
          <button className="font-bold text-sm px-4 py-2 rounded-lg" style={{ background: "#FDE047", color: "#1E3A8A" }}>
            Sign In
          </button>
        </div>
      </header>

      <main className="relative z-10 px-4 pt-14 pb-24">
        <div className="max-w-5xl mx-auto text-center">

          <div className="mb-8 flex justify-center">
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold"
              style={{ background: "rgba(253,224,71,.1)", border: "1px solid rgba(253,224,71,.25)", color: "#FDE047" }}>
              <Zap size={14} />
              Powered by TailorWiz
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight tracking-tight">
            <span className="block">
              Find the{" "}
              <span style={{ color: "#FDE047" }}>Hiring Manager</span>
              {" "}or Team
            </span>
            <span className="block">
              for{" "}
              <span className="underline decoration-yellow-400 decoration-4 underline-offset-8">ANY</span>
              {" "}Job in Just Minutes.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-blue-200/90 mb-12 max-w-2xl mx-auto font-light">
            Stop waiting for the ATS to pass your resume to a human. Go straight to the
            decision-maker with a precision-tailored resume that drives a
            <br />
            <span className="font-semibold text-white">70% interview acquisition rate on all applications.</span>
          </p>

          <div className="max-w-3xl mx-auto">
            <div className="rounded-3xl p-2 border" style={{ background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.15)", backdropFilter: "blur(12px)" }}>
              <div className="flex flex-col md:flex-row gap-2 items-stretch">
                <div className="flex-1 relative">
                  <textarea
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-2xl px-6 py-4 text-base md:text-lg text-white placeholder-transparent focus:outline-none bg-transparent"
                  />
                  {!query && (
                    <div className="absolute top-0 left-0 px-6 py-4 text-base md:text-lg text-white/35 pointer-events-none select-none">
                      {ghostText}<span className="opacity-70">|</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => search(query)}
                  disabled={loading || !query.trim()}
                  className="md:self-stretch h-auto px-8 py-4 font-black rounded-2xl shadow-lg transform hover:scale-[1.02] transition-all duration-200 text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ background: "#FDE047", color: "#1E3A8A" }}>
                  {loading ? <Loader2 size={20} className="spin" /> : <Search size={20} />}
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
            {loading && queued && (
              <div className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
                style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", color: "#86efac" }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e", flexShrink: 0 }} />
                Search queued — JobIntel 360 is actively scanning LinkedIn for contacts
              </div>
            )}
            {loading && (
              <SearchProgress progress={progress} stage={progressStage} theme="dark" startedAt={startedAt} />
            )}
            {!loading && (
              <p className="text-blue-300/80 text-sm mt-4">
                Paste a job URL, a description, or both — we will find the decision-maker.
              </p>
            )}
          </div>

          {error && (
            <div className="max-w-3xl mx-auto mt-6 rounded-2xl px-6 py-4 text-sm text-left"
              style={{ background: "rgba(239,68,68,.15)", border: "1px solid rgba(239,68,68,.3)", color: "#fca5a5" }}>
              {error}
            </div>
          )}

          {result && (
            <div className="max-w-3xl mx-auto mt-6 text-left">
              <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", backdropFilter: "blur(12px)" }}>
                <div className="px-5 py-3.5 flex items-center justify-between" style={{ background: "rgba(0,0,0,0.25)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <div>
                    <span className="text-white font-black text-sm">{result.company ?? "Results"}</span>
                    {result.job_title && <span className="text-white/50 text-xs ml-2">· {result.job_title}</span>}
                    {result.location  && <span className="text-white/50 text-xs ml-2">· {result.location}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => exportHiringManagerPdf(result)}
                      className="text-xs font-bold px-3 py-1 rounded-full transition-opacity hover:opacity-80"
                      style={{ background: "rgba(253,224,71,0.2)", border: "1px solid rgba(253,224,71,0.5)", color: "#fde047" }}>
                      Download PDF
                    </button>
                    <button onClick={() => { reset(); setQuery(""); }} className="text-xs font-bold text-white/40 hover:text-white transition-colors">
                      New Search
                    </button>
                  </div>
                </div>
                {result.contacts?.map(c => <ContactCard key={c.rank} c={c} />)}
              </div>
            </div>
          )}

          <div className="mt-16 flex flex-wrap gap-4 justify-center">
            {[
              { Icon: Clock,  label: "Results in minutes" },
              { Icon: Users,  label: "Verified contacts" },
              { Icon: Zap,    label: "10-20x response rate" },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-2 px-5 py-2.5 rounded-full border"
                style={{ background: "rgba(255,255,255,.07)", borderColor: "rgba(255,255,255,.12)", color: "white" }}>
                <Icon size={16} color="#FDE047" />
                <span className="text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>

        </div>
      </main>

      <footer className="relative z-10 px-6 py-6 text-center text-blue-300/40 text-sm">
        &copy; {year} HiringManagerID &mdash; A TailorWiz product
      </footer>
    </div>
  );
}
